import { useEffect, useState, useCallback, useRef } from 'react';

// Intervalle de vérification de la version (5 minutes)
const CHECK_INTERVAL = 5 * 60 * 1000;

/**
 * Récupère la version courante depuis le manifest, sans passer par le cache.
 */
const fetchManifestVersion = async () => {
    const response = await fetch(`/manifest.json?t=${Date.now()}`, { cache: 'no-store' });
    const manifest = await response.json();
    return manifest.version;
};

/**
 * Vide tous les caches du site (Cache Storage + service workers éventuels)
 * puis recharge la page pour récupérer les derniers assets.
 */
const clearCachesAndReload = async (latestVersion) => {
    try {
        // Mémoriser la nouvelle version avant le reload
        if (latestVersion) {
            localStorage.setItem('appVersion', latestVersion);
        }

        // Supprimer les caches de l'API Cache Storage (PWA / service worker)
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }

        // Désinscrire d'éventuels service workers
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((registration) => registration.unregister()));
        }
    } catch (error) {
        console.error('Erreur lors du nettoyage du cache :', error);
    } finally {
        // Recharge la page (les caches étant vidés, les nouveaux assets sont récupérés)
        window.location.reload();
    }
};

/**
 * Hook de détection des mises à jour.
 *
 * Ne force plus le rechargement : expose `updateAvailable` et `applyUpdate`
 * pour permettre d'afficher une notification à l'utilisateur, qui choisit
 * quand relancer le site (et ainsi vider le cache).
 *
 * @returns {{ updateAvailable: boolean, latestVersion: string|null, applyUpdate: () => void }}
 */
const useCheckForUpdates = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [latestVersion, setLatestVersion] = useState(null);
    const latestVersionRef = useRef(null);

    const check = useCallback(async () => {
        try {
            const version = await fetchManifestVersion();
            const currentVersion = localStorage.getItem('appVersion');

            // Première visite : on enregistre la version sans rien faire
            if (!currentVersion) {
                localStorage.setItem('appVersion', version);
                return;
            }

            if (currentVersion !== version) {
                latestVersionRef.current = version;
                setLatestVersion(version);
                setUpdateAvailable(true);
            }
        } catch (error) {
            console.error('Erreur lors de la vérification des mises à jour :', error);
        }
    }, []);

    useEffect(() => {
        check();

        // Vérification périodique pendant que l'app est ouverte
        const intervalId = setInterval(check, CHECK_INTERVAL);

        // Vérification quand l'utilisateur revient sur l'onglet
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                check();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('visibilitychange', handleVisibility);
        };
    }, [check]);

    const applyUpdate = useCallback(() => {
        clearCachesAndReload(latestVersionRef.current);
    }, []);

    return { updateAvailable, latestVersion, applyUpdate };
};

export default useCheckForUpdates;
